import { Tooltip } from '@mantine/core';
import {
  IconSetup, IconQuote, IconTimer, IconSupply, IconExecute, IconPackage, IconTruck, IconDelivered,
} from '../../components/icons/CncIcons';

export const ORDER_STATUSES = [
  'Order Set-up',
  'In Quotation',
  // 'Order Started' is a distinct pipeline node (added 2026-04) so the Ongoing Order screen's
  // first Step-table row has a dedicated highlight. It sits between 'In Quotation' and the
  // supplying phase so the existing downstream statuses keep their meaning.
  'Order Started',
  'Supplying Consumables',
  'In Execution',
  'Preparing to Ship',
  'Shipped',
  'Delivered',
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

const ICON_MAP: Record<OrderStatus, React.ComponentType<{ size?: number }>> = {
  'Order Set-up': IconSetup,
  'In Quotation': IconQuote,
  'Order Started': IconTimer,
  'Supplying Consumables': IconSupply,
  'In Execution': IconExecute,
  'Preparing to Ship': IconPackage,
  'Shipped': IconTruck,
  'Delivered': IconDelivered,
};

export default function OrderStatusBar({
  current,
  onSelect,
  disabled,
}: {
  current: OrderStatus;
  onSelect?: (s: OrderStatus) => void;
  disabled?: boolean;
}) {
  const currentIdx = ORDER_STATUSES.indexOf(current);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${ORDER_STATUSES.length}, 1fr)`,
        gap: 0,
        padding: '12px 8px',
        borderRadius: 6,
        border: '1px solid var(--mantine-color-default-border)',
        marginBottom: 16,
      }}
    >
      {ORDER_STATUSES.map((s, i) => {
        const Icon = ICON_MAP[s];
        const state: 'done' | 'current' | 'future' =
          i < currentIdx ? 'done' : i === currentIdx ? 'current' : 'future';
        const color =
          state === 'current' ? 'var(--mantine-color-orange-6)' :
          state === 'done' ? 'var(--mantine-color-text)' :
          'var(--mantine-color-dimmed)';
        const opacity = state === 'future' ? 0.4 : 1;
        const bg = state === 'current' ? 'var(--mantine-color-orange-light)' : 'transparent';
        const interactive = !disabled && !!onSelect;
        return (
          <Tooltip key={s} label={s} withArrow openDelay={300}>
            <div
              onClick={() => interactive && onSelect?.(s)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
                padding: '8px 4px',
                borderRadius: 4,
                cursor: interactive ? 'pointer' : 'default',
                color,
                opacity,
                background: bg,
                fontWeight: state === 'current' ? 600 : 400,
                fontSize: 12,
                textAlign: 'center',
                position: 'relative',
              }}
            >
              <Icon size={22} />
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>{s}</span>
              {i < ORDER_STATUSES.length - 1 && (
                <div
                  style={{
                    position: 'absolute',
                    top: 20,
                    right: -4,
                    width: 8,
                    height: 2,
                    background: i < currentIdx ? 'var(--mantine-color-orange-6)' : 'var(--mantine-color-default-border)',
                  }}
                />
              )}
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
}
